import React, { FC, useContext, useState } from 'react';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp';
import { faDownLeftAndUpRightToCenter } from '@fortawesome/free-solid-svg-icons/faDownLeftAndUpRightToCenter';
import { faPencil } from '@fortawesome/free-solid-svg-icons/faPencil';
import { faTrophy } from '@fortawesome/free-solid-svg-icons/faTrophy';
import { faUpRightAndDownLeftFromCenter } from '@fortawesome/free-solid-svg-icons/faUpRightAndDownLeftFromCenter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from 'rc-tooltip';

import { NavigationContext } from 'buyside/components/presentation/MainSiteLayout/mainSiteLayout';
import ErrorBoundary from 'buyside/components/presentation/ErrorBoundary/errorBoundary';
import NavigationFeedback from 'buyside/components/presentation/MainSiteNavigation/SideNavigation/SideNavigationFeedback/sideNavigationFeedback';
import Button, {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from 'buyside/components/presentation/Button/button';
import LnkdAuthRedirectButton from 'buyside/components/presentation/LnkdAuthRedirectButton/lnkdAuthRedirectButton';
import SideNavigationContestBanner from 'buyside/components/presentation/MainSiteNavigation/SideNavigation/SideNavigationContestBanner/sideNavigationContestBanner';

import { accessIdeasUrl, newResearchUrl } from 'buyside/util/urlHelper';
import { useIsHome, useIsHomeWithPage } from 'buyside/util/hooks/useWindowLocation';
import { useIsMobile } from 'buyside/util/hooks/useIsMobile';

import { useFeaturedContestQuery } from 'interfaces/graphql-typings';

import styles from './sideNavigationActionsStyles.scss';

const SideNavigationActions: FC<{
  canWriteIdeas: boolean;
  canViewIdeas: boolean;
  currentUserId: string;
}> = ({ canWriteIdeas, canViewIdeas, currentUserId }) => {
  const [actionOpen, toggleAction] = useState(true);

  const isMobile = useIsMobile();
  const homePreRedirect = useIsHome();
  const homePostRedirect = useIsHomeWithPage();
  const isHome = homePreRedirect || homePostRedirect;

  const { sideNavigationOpen } = useContext(NavigationContext) || {};

  const featuredContests = useFeaturedContestQuery().data?.featuredContests;

  if (sideNavigationOpen)
    return (
      <div className={styles.navigationActions}>
        <button className={styles.actionIcon} onClick={() => toggleAction(state => !state)}>
          <FontAwesomeIcon
            icon={actionOpen ? faDownLeftAndUpRightToCenter : faUpRightAndDownLeftFromCenter}
          />
        </button>
        {actionOpen ? (
          <>
            {featuredContests?.length ? (
              <SideNavigationContestBanner contests={featuredContests} />
            ) : null}
            {canViewIdeas ? null : (
              <Button
                size={isMobile ? BUTTON_SIZES.SMALL : BUTTON_SIZES.TINY}
                text="Upgrade"
                type={BUTTON_TYPES.PRIMARY}
                url={accessIdeasUrl()}
                width={BUTTON_WIDTHS.FULL}
              />
            )}
          </>
        ) : null}
        {canWriteIdeas ? (
          <Button
            faIcon={faPencil}
            size={isMobile ? BUTTON_SIZES.SMALL : BUTTON_SIZES.TINY}
            text="Submit Research"
            type={BUTTON_TYPES.MAJOR}
            url={newResearchUrl()}
            width={BUTTON_WIDTHS.FULL}
          />
        ) : null}
        {actionOpen && (
          <>
            <LnkdAuthRedirectButton />
            {isHome && (
              <ErrorBoundary pageName="navigation feedback">
                <NavigationFeedback currentUserId={currentUserId} />
              </ErrorBoundary>
            )}
          </>
        )}
      </div>
    );

  return (
    <div className={styles.collapsedNavigationActions}>
      {featuredContests?.length ? (
        <Tooltip
          trigger={['hover', 'click']}
          overlay={<SideNavigationContestBanner contests={featuredContests} />}
          overlayClassName={styles.contestBannerTooltip}
          placement="rightBottom"
        >
          <button data-type="contest" className={styles.collapsedActionButton}>
            <FontAwesomeIcon icon={faTrophy} />
          </button>
        </Tooltip>
      ) : null}
      {canViewIdeas ? null : (
        <Tooltip placement="right" overlay={<>Upgrade</>}>
          <a data-type="upgrade" className={styles.collapsedActionButton} href={accessIdeasUrl()}>
            <FontAwesomeIcon icon={faArrowUp} />
          </a>
        </Tooltip>
      )}
      {canWriteIdeas ? (
        <Tooltip placement="right" overlay={<>Submit Research</>}>
          <a
            data-type="submit-research"
            className={styles.collapsedActionButton}
            href={newResearchUrl()}
          >
            <FontAwesomeIcon icon={faPencil} />
          </a>
        </Tooltip>
      ) : null}
    </div>
  );
};

export default SideNavigationActions;
